import React from 'react';
import { Payment } from '../../types/payment';
import { formatCurrency } from '../../utils/formatters';
import { formatDate, formatDateTime } from '../../utils/formatters';
import { generateReceiptPDF } from '../../utils/receiptPdfGenerator';

const thStyle: React.CSSProperties = {
  padding: '10px 14px', fontFamily: "'Inter', sans-serif", fontSize: 8,
  fontWeight: 500, letterSpacing: '0.15em', textTransform: 'uppercase',
  color: '#6b6a5c', borderBottom: '1px solid rgba(92,78,51,0.25)',
  textAlign: 'left', background: '#faf7ee',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 14px', fontFamily: "'Inter', sans-serif", fontSize: 12,
  color: '#5b5a52', borderBottom: '1px solid rgba(239,232,213,0.6)',
};

interface TransactionReceiptProps {
  payment: Payment | { payments: Payment[]; totalAmount: number };
  companyName?: string;
  companyAddress?: string;
  companyPhone?: string;
  companyEmail?: string;
  companyLogo?: string;
  onClose: () => void;
  onPrint: () => void;
}

export const TransactionReceipt: React.FC<TransactionReceiptProps> = ({
  payment,
  companyName = 'Your Company Name',
  companyAddress = 'Your Company Address',
  companyPhone = '+123 456 7890',
  companyEmail = 'info@yourcompany.com',
  companyLogo,
  onClose,
  onPrint
}) => {
  const isMultiPayment = 'payments' in payment && Array.isArray((payment as any).payments);
  const payments: Payment[] = isMultiPayment ? (payment as { payments: Payment[]; totalAmount: number }).payments : [payment as Payment];
  const firstPayment = payments[0];
  const totalAmount = isMultiPayment ? (payment as { payments: Payment[]; totalAmount: number }).totalAmount : (payment as Payment).amount;

  const getRecipient = () => {
    if (firstPayment.customer) return firstPayment.customer;
    if (firstPayment.vendor) return firstPayment.vendor;
    return null;
  };

  const recipient = getRecipient();
  const isBillPayment = firstPayment.vendor && firstPayment.bill;

  const handleExportPDF = () => {
    generateReceiptPDF({ payment, companyName, companyAddress, companyPhone, companyEmail, companyLogo });
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(7,7,15,0.85)',
      overflowY: 'auto', zIndex: 50,
    }}>
      <div style={{
        position: 'relative', top: 40, margin: '0 auto',
        width: '100%', maxWidth: 900,
        background: '#ffffff', border: '1px solid rgba(92,78,51,0.25)',
        borderRadius: 8, overflow: 'hidden',
      }}>
        {/* Receipt Document */}
        <div id="transaction-receipt" style={{ padding: '40px 48px', background: '#ffffff' }}>
          {/* Header: Logo + Company Info */}
          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 40 }}>
            <div style={{
              width: 72, height: 72, borderRadius: '50%',
              background: 'rgba(92,78,51,0.15)', border: '1px solid rgba(92,78,51,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginRight: 20, flexShrink: 0,
            }}>
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 700, color: '#d97706' }}>
                {companyName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
              </span>
            </div>
            <div>
              <h1 style={{ fontFamily: "'Inter', sans-serif", fontSize: 26, fontWeight: 600, color: '#1c2033', margin: '0 0 8px' }}>
                {companyName}
              </h1>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', lineHeight: 1.6 }}>
                <p style={{ margin: 0 }}>{companyAddress}</p>
                <p style={{ margin: 0 }}>{companyPhone}</p>
                <p style={{ margin: 0, color: '#d97706' }}>{companyEmail}</p>
              </div>
            </div>
          </div>

          {/* Title */}
          <div style={{ textAlign: 'center', marginBottom: 36 }}>
            <h2 style={{
              fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 500,
              letterSpacing: '0.4em', textTransform: 'uppercase', color: '#d97706', margin: 0,
            }}>
              Payment Receipt
            </h2>
            <div style={{ height: 1, background: 'linear-gradient(90deg, transparent, rgba(217,119,6,0.35), transparent)', marginTop: 12 }} />
          </div>

          {/* Payment Details Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20, marginBottom: 32 }}>
            <div>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6b6a5c', margin: '0 0 6px' }}>Payment Date</p>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, fontWeight: 500, color: '#1c2033', margin: 0 }}>
                {formatDate(firstPayment.paymentDate)}
              </p>
            </div>
            <div>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6b6a5c', margin: '0 0 6px' }}>Transaction Number</p>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, fontWeight: 500, color: '#1c2033', margin: 0 }}>
                {firstPayment.transactionNumber}
              </p>
            </div>
            {firstPayment.reference && (
              <div>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6b6a5c', margin: '0 0 6px' }}>Reference Number</p>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, fontWeight: 500, color: '#1c2033', margin: 0 }}>
                  {firstPayment.reference}
                </p>
              </div>
            )}
            {isMultiPayment && payments.length > 1 ? (
              <div>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6b6a5c', margin: '0 0 6px' }}>Invoices Paid</p>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, fontWeight: 500, color: '#d97706', margin: 0 }}>
                  {payments.length} Invoices
                </p>
              </div>
            ) : (firstPayment.invoice || firstPayment.bill) && (
              <div>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6b6a5c', margin: '0 0 6px' }}>
                  {firstPayment.invoice ? 'Invoice Number' : 'Bill Number'}
                </p>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, fontWeight: 500, color: '#d97706', margin: 0 }}>
                  {firstPayment.invoice ? firstPayment.invoice.invoiceNumber : firstPayment.bill?.billNumber}
                </p>
              </div>
            )}
            <div>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6b6a5c', margin: '0 0 6px' }}>Payment Mode</p>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, fontWeight: 500, color: '#1c2033', margin: 0, textTransform: 'capitalize' }}>
                {firstPayment.paymentMethod?.replace('_', ' ') || 'Cash'}
              </p>
            </div>
          </div>

          {/* Amount Highlight */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 32 }}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(107,191,138,0.12), rgba(107,191,138,0.06))',
              border: '1px solid rgba(107,191,138,0.3)',
              borderRadius: 8, padding: '16px 28px', textAlign: 'right',
            }}>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6bbf8a', margin: '0 0 6px' }}>
                {isBillPayment ? 'Amount Paid' : 'Amount Received'}
              </p>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 32, fontWeight: 700, color: '#6bbf8a', margin: 0, lineHeight: 1 }}>
                {formatCurrency(totalAmount)}
              </p>
            </div>
          </div>

          {/* Recipient */}
          {recipient && (
            <div style={{ marginBottom: 28 }}>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#d97706', marginBottom: 10, marginTop: 0 }}>
                {isBillPayment ? 'Payment To' : 'Received From'}
              </p>
              <div style={{ background: '#faf7ee', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: '12px 16px' }}>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 500, color: '#1c2033', margin: '0 0 4px' }}>
                  {recipient.name}
                </p>
                {recipient.email && (
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', margin: 0 }}>
                    {recipient.email}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Payment For Table */}
          {payments.some((p) => p.invoice || p.bill) && (
            <div style={{ marginBottom: 28 }}>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#d97706', marginBottom: 10, marginTop: 0 }}>
                Payment For
              </p>
              <div style={{ overflowX: 'auto', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>{firstPayment.invoice ? 'Invoice Number' : 'Bill Number'}</th>
                      <th style={thStyle}>{firstPayment.invoice ? 'Invoice Date' : 'Bill Date'}</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>{firstPayment.invoice ? 'Invoice Amount' : 'Bill Amount'}</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Payment Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((paymentItem, index) => (
                      (paymentItem.invoice || paymentItem.bill) && (
                        <tr key={index}>
                          <td style={{ ...tdStyle, color: '#d97706' }}>
                            {paymentItem.invoice ? paymentItem.invoice.invoiceNumber : paymentItem.bill?.billNumber}
                          </td>
                          <td style={tdStyle}>{formatDate(paymentItem.paymentDate)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>
                            {formatCurrency(paymentItem.invoice ? paymentItem.invoice.total : paymentItem.bill?.total || 0)}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 500, color: '#1c2033' }}>
                            {formatCurrency(paymentItem.amount)}
                          </td>
                        </tr>
                      )
                    ))}
                    {isMultiPayment && (
                      <tr style={{ background: '#faf7ee' }}>
                        <td colSpan={3} style={{ ...tdStyle, color: '#6b6a5c', fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                          Total Payment
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 700, color: '#d97706' }}>
                          {formatCurrency(totalAmount)}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Notes */}
          {firstPayment.notes && (
            <div style={{ marginBottom: 24 }}>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#d97706', marginBottom: 8, marginTop: 0 }}>
                Notes
              </p>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52', background: '#faf7ee', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: '10px 14px', margin: 0 }}>
                {firstPayment.notes}
              </p>
            </div>
          )}

          {/* Footer */}
          <div style={{ borderTop: '1px solid rgba(92,78,51,0.15)', paddingTop: 20, textAlign: 'center' }}>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', margin: '0 0 4px' }}>
              This receipt was generated on {formatDateTime(new Date())}
            </p>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', margin: 0 }}>
              Transaction ID: {firstPayment.id}
            </p>
            {isMultiPayment && (
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, color: '#8a8778', marginTop: 4 }}>
                Multi-payment transaction covering {payments.length} invoices
              </p>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 12,
          padding: '16px 24px', borderTop: '1px solid rgba(92,78,51,0.15)',
          background: '#faf7ee',
        }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '9px 20px', background: 'none',
              border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4,
              fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 400,
              letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6b6a5c', cursor: 'pointer',
            }}
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleExportPDF}
            style={{
              padding: '9px 20px',
              background: 'rgba(92,78,51,0.15)', border: '1px solid rgba(92,78,51,0.3)', borderRadius: 4,
              fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500,
              letterSpacing: '0.15em', textTransform: 'uppercase', color: '#d97706', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export PDF
          </button>
          <button
            type="button"
            onClick={onPrint}
            style={{
              padding: '9px 20px',
              background: 'linear-gradient(135deg, #d97706, #b45309)', border: 'none', borderRadius: 4,
              fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500,
              letterSpacing: '0.15em', textTransform: 'uppercase', color: '#1c2033', cursor: 'pointer',
            }}
          >
            Print Receipt
          </button>
        </div>
      </div>
    </div>
  );
};
